import { GameState } from "../state";
import { TAU } from "../../core/math";
import { ENEMIES } from "../../data/enemies";
import { abilityToPattern, BOSSES, bossEnemyDef, abilitiesForBoss, stageBossHpScale } from "../../data/bosses";
import { makeEnemy } from "./spawner";

// Drives the active boss: cycles its abilities on a cadence and fires the
// corresponding attack pattern. The boss itself moves/contacts via the normal
// enemy AI; this only adds the ranged/summon attacks.
export function updateBoss(s: GameState, dt: number): void {
  const b = s.bossEnemy;
  if (!b || !b.alive || !b.bossState) return;
  const bs = b.bossState;

  if (b.hitFlash > 0) b.hitFlash -= dt;

  // Glitched-mode intro: the expected Day boss stands doomed and invulnerable
  // until the Firewall arrives to destroy it and turn on PCKT.
  if (bs.scripted === "glitched_intro") {
    bs.scriptTimer = (bs.scriptTimer ?? 0) - dt;
    if (bs.scriptTimer <= 0) glitchedFirewallTakeover(s, b);
    return;
  }

  bs.timer -= dt;
  if (bs.timer <= 0) {
    const ability = bs.abilities[bs.index % bs.abilities.length] ?? "burst";
    bs.index++;
    cast(s, b.x, b.y, abilityToPattern(ability), bs.isZeroDay, b.def.color);
    // Zero Day attacks fastest; mini-bosses are the most forgiving.
    bs.timer = bs.isZeroDay ? 1.7 : bs.isMini ? 3.2 : 2.7;
  }
}

/**
 * The Firewall arrives, destroys the doomed Day boss on-screen, then — its
 * perception corrupted — becomes the real hostile boss for this fight.
 */
function glitchedFirewallTakeover(s: GameState, doomed: import("../entities").Enemy): void {
  const fw = BOSSES.firewall;
  s.addShake(16);
  s.ring(doomed.x, doomed.y, fw.color, 44, 440);
  s.addFloat(doomed.x, doomed.y - 50, "FIREWALL: THREAT NEUTRALIZED", fw.color, 18);
  // Remove the doomed boss without granting rewards or triggering "boss down".
  doomed.alive = false;
  const idx = s.enemies.indexOf(doomed);
  if (idx >= 0) s.enemies.splice(idx, 1);

  const e = makeEnemy(bossEnemyDef(fw), s.arenaW / 2, s.arenaH * 0.25, stageBossHpScale(s.zone));
  e.bossState = {
    name: fw.name,
    abilities: abilitiesForBoss("firewall", s.rng, 0),
    timer: 1.2,
    index: 0,
    isZeroDay: false,
    glitchedFirewall: true,
  };
  s.enemies.push(e);
  s.bossEnemy = e;
  s.addFloat(s.player.x, s.player.y - 104, "FIREWALL MISREADS PCKT AS THE THREAT", fw.color, 16);
  s.addFloat(s.player.x, s.player.y - 80, "INCOMING: FIREWALL", "#ff5454", 24);
}

function cast(
  s: GameState,
  x: number,
  y: number,
  pattern: "burst" | "volley" | "summon" | "blink",
  zeroDay: boolean,
  color: string,
): void {
  const p = s.player;
  switch (pattern) {
    case "burst": {
      const n = zeroDay ? 22 : 14;
      const base = s.rng.angle();
      for (let i = 0; i < n; i++) {
        spawnHostile(s, x, y, base + (i / n) * TAU, 240, 10, color);
      }
      s.ring(x, y, color, 16, 200);
      break;
    }
    case "volley": {
      const aim = Math.atan2(p.y - y, p.x - x);
      const shots = zeroDay ? 7 : 5;
      for (let i = 0; i < shots; i++) {
        const t = i / (shots - 1) - 0.5;
        spawnHostile(s, x, y, aim + t * 0.6, 340, 12, color);
      }
      break;
    }
    case "summon": {
      const count = zeroDay ? 6 : 4;
      const def = s.rng.chance(0.5) ? ENEMIES.worm : ENEMIES.virus;
      const hpScale = 1 + (s.zone - 1) * 0.25;
      for (let i = 0; i < count; i++) {
        const a = s.rng.angle();
        const r = s.rng.range(40, 110);
        s.enemies.push(makeEnemy(def, x + Math.cos(a) * r, y + Math.sin(a) * r, hpScale));
      }
      s.ring(x, y, color, 12, 160);
      break;
    }
    case "blink": {
      const b = s.bossEnemy;
      if (!b) break;
      const a = s.rng.angle();
      const r = s.rng.range(160, 280);
      s.ring(b.x, b.y, color, 14, 220);
      b.x = Math.max(20, Math.min(s.arenaW - 20, p.x + Math.cos(a) * r));
      b.y = Math.max(20, Math.min(s.arenaH - 20, p.y + Math.sin(a) * r));
      s.ring(b.x, b.y, color, 14, 220);
      break;
    }
  }
}

function spawnHostile(
  s: GameState,
  x: number,
  y: number,
  angle: number,
  speed: number,
  damage: number,
  color: string,
): void {
  s.projectiles.push({
    alive: true,
    x,
    y,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    radius: 7,
    damage,
    pierce: 0,
    life: 4,
    color,
    crit: false,
    knockback: 0,
    hostile: true,
    hitSet: new Set(),
  });
}
